/* import __COLOCATED_TEMPLATE__ from './modal-manager.hbs'; */
import { getOwner } from '@ember/application';
import { assert } from '@ember/debug';
import { notEmpty, readOnly } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { CLOSE_CLICK, CLOSE_ESC } from '../../services/modal';
import { computed } from '@ember/object';
const KEY_ESC = 27;
export const MODAL_POSITION = {
  TOP: 'TOP'
};
const positionClassNameMap = {
  [MODAL_POSITION.TOP]: 'modal-layout--position-top'
};

/**

## Overview

**Do not use this component directly!** It is intended to exist only in `application.hbs`.

Modal Manager controls the display of modal windows over the application. It manages the
veil/scrim behind the modal window, as well as click, ESC key, navigation, and focus events.

It works in conjunction with the modal service, which actually holds the state.

## Usage

```handlebars
{{! Again, don't do this anywhere but application.hbs! }}
{{modals/modal-manager}}
```

## How to show a modal

1. Create a component, i.e. `{{my-modal}}`. 99.9% percent of the time, you will include
   the `{{dialog-modal}}` component inside of it.
2. In response to some user action, call `open('my-modal')` on the modal service.

    ```javascript
    // some other route or component
    modal: Ember.inject.service(),

    actions: {
      openModal() {
        this.get('modal').open(MyModal, { someData: 108 })
          .then(() =>
            // do something when the modal closes
          );
      }
    }
    ```

3. Your `my-modal` component will receive five properties:
    1. A `modalOptions` property, which is the second argument to `open()`.
       So you can access `someData` with `this.get('modalOptions.someData')`.
    2. A `close` action, which closes the modal.
    3. A `setCloseHandler` action, which allows the component to disallow closing
       the modal when clicking on the veil, pressing ESC, or navigating away.
    4. A `lockModalHeight` action, which allows the component to fix its vertical
       height before making changes to its contents, to avoid vertical bouncing.
    5. An `unlockModalHeight` action, which returns to the default vertical sizing.

*/

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  router: service(),
  modal: service(),
  hasActiveModal: notEmpty('modal.stack'),
  modalIsOpen: readOnly('modal.isOpen'),
  lastModal: readOnly('modal.stack.lastObject'),
  init(...args) {
    this._super(...args);
    assert("<Modals::ModalManager> should only be mounted in the host app's application.hbs", !this.router.currentRoute ||
    // Integration tests need to mount this and don't have a current route
    getOwner(this) === getOwner(this.modal));
  },
  zIndexClassName: computed('hasActiveModal', 'lastModal.options.zIndexClass', function () {
    if (this.get('hasActiveModal')) {
      const lastModal = this.get('lastModal');
      if (lastModal.options?.zIndexClass) {
        return this.lastModal.options.zIndexClass;
      }
    }
    return '';
  }),
  positionClassName: computed('hasActiveModal', 'lastModal', function () {
    if (this.get('hasActiveModal')) {
      const lastModal = this.get('lastModal');
      if (lastModal.metaOptions.position) {
        return positionClassNameMap[lastModal.metaOptions.position] || '';
      }
    }
    return '';
  }),
  didInsertElement(...args) {
    this._super(...args);
    this._windowKeydownListener = e => this.handleEscKey(e);
    this._mousewheelListener = e => this.handleScrolling(e);
    window.addEventListener('keydown', this._windowKeydownListener);
    this.element.addEventListener('mousewheel', this._mousewheelListener);
  },
  willDestroyElement(...args) {
    this._super(...args);
    window.removeEventListener('keydown', this._windowKeydownListener);
    this.element.removeEventListener('mousewheel', this._mousewheelListener);
  },
  click(e) {
    if (e.target.classList.contains('modal-layout') || e.target.classList.contains('modal-layout__modal-container')) {
      this.get('modal').maybeCloseOnEvent(CLOSE_CLICK);
    }
  },
  handleEscKey(e) {
    if (this.get('modal.isOpen') && e.keyCode === KEY_ESC) {
      this.get('modal').maybeCloseOnEvent(CLOSE_ESC);
    }
  },
  // disallow scrolling behind the modal
  handleScrolling(e) {
    if (e.target.classList.contains('modal-layout')) {
      e.preventDefault();
    }
  },
  _veilTouchStarted: false,
  touchStart(e) {
    if (e.target.classList.contains('modal-layout')) {
      this._veilTouchStarted = true;
    }
  },
  touchMove(e) {
    // Disable touch scrolling if we started the touch on the veil
    if (this._veilTouchStarted && e.target.classList.contains('modal-layout')) {
      e.preventDefault();
    }
  },
  touchEnd() {
    this._veilTouchStarted = false;
  },
  actions: {
    close(...args) {
      this.get('modal').close(...args);
    },
    /*
       Modals are vertically centered on the screen, but the height of the modal content can change
       in certain cases in response to user behavior, which causes jarring vertical movement. These
       helper functions allow modal implementations to lock and unlock the height of the modal prior
       to updating their contents, to avoid that vertical movement.
    */
    lockModalHeight() {
      const modalWrappers = this.element.querySelectorAll('.dialog--modal');
      const modalWrapper = modalWrappers[modalWrappers.length - 1];
      modalWrapper.style.height = `${modalWrapper.offsetHeight}px`;
    },
    unlockModalHeight() {
      const modalWrappers = this.element.querySelectorAll('.dialog--modal');
      const modalWrapper = modalWrappers[modalWrappers.length - 1];
      modalWrapper.style.height = 'auto';
    }
  }
});